/**
 * Real-club activation path: sequential actions with nested substeps.
 * Predicates read from getClinicSetupStatus (+ optional local flags).
 */

import L from "../constants/baysportLabels";
import { isCoreBottomTab, MORE_ADMIN_NAV, ADMIN_BOTTOM_TABS } from "../utils/adminNav";

const NAV_LABELS = {
  directions: L.directions,
  venues: L.venues,
  groups: L.groups,
  memberships: L.memberships,
  clients: L.clients,
  journal: L.journal,
  overview: L.summary,
  chat: "Чат",
  team: L.team,
  finance: "Финансы",
  settings: "Настройки",
};

export function matchesActivationRoute(pathname, search = "", route = "") {
  if (!route) return false;
  const [routePath, routeQuery] = route.split("?");
  const path = (pathname || "").replace(/\/$/, "") || "/manage/app";
  const base = routePath.replace(/\/$/, "");
  if (path !== base && !path.startsWith(`${base}/`)) return false;
  if (!routeQuery) return true;
  const required = new URLSearchParams(routeQuery);
  const actual = new URLSearchParams(search || "");
  for (const [key, value] of required) {
    if (actual.get(key) !== value) return false;
  }
  return true;
}

const NAV_ITEM_PATHS = {
  directions: "/manage/app/directions",
  venues: "/manage/app/venues",
  groups: "/manage/app/groups",
  memberships: "/manage/app/memberships",
  clients: "/manage/app/clients",
  journal: "/manage/app/journal",
  overview: "/manage/app",
  chat: "/manage/app/chat",
  team: "/manage/app/team",
  finance: "/manage/app/finance",
  settings: "/manage/app/settings",
  more: "/manage/app/more",
};

/** Коротко: что это и зачем — для nav-подсказки в туре */
const NAV_BLURBS = {
  direction:
    "Вид спорта секции — бокс, дзюдо, ОФП. Без направления не собрать группу.",
  venue: "Место занятий. Зал привязывается к группе и попадает в расписание.",
  group: "Связка направления и зала. Отсюда — расписание и журнал.",
  schedule: "Дни и время занятий группы. Без расписания журнал пустой.",
  plan: "Шаблон абонемента: сколько занятий и по какой цене.",
  client: "Карточка ученика — имя, телефон, связь с родителем.",
  issue: "Привязка тарифа к ученику, чтобы списывать занятия.",
  attendance: "Отметка в журнале — списывает занятие с абонемента.",
  parents: "Ссылка для самостоятельной регистрации родителей и учеников в приложении.",
  overview:
    "Главный экран: KPI дня, ближайшие занятия, должники и быстрые действия.",
  chat: "Диалоги с родителями и учениками — ответы, напоминания, рассылки.",
  team: "Кто имеет доступ к CRM: тренеры, администраторы, роли и приглашения.",
  finance:
    "Выручка, должники, платежи и расходы — контроль денег клуба.",
  settings:
    "Название клуба, регистрация, уведомления, брендинг и интеграции.",
};

export function matchesNavItemRoute(pathname, navItem) {
  const base = NAV_ITEM_PATHS[navItem];
  if (!base) return false;
  const path = (pathname || "").replace(/\/$/, "") || "/manage/app";
  return path === base || path.startsWith(`${base}/`);
}

export const ACTIVATION_STEPS = [
  {
    id: "direction",
    index: 1,
    title: "Направление",
    short: "Добавьте вид спорта",
    navItem: "directions",
    route: "/manage/app/directions",
    hint: "Добавьте направление — вид спорта секции.",
    success: "Направление есть.",
    predicate: "hasDirection",
    substeps: [
      {
        id: "dir_open",
        title: "Новое направление",
        hint: "Нажмите «+» или «Добавить направление».",
        target: '[data-act-sub="dir_open"]',
      },
      {
        id: "dir_fill",
        title: "Название",
        hint: "Введите вид спорта — дзюдо, бокс, ОФП…",
        target: '[data-act-sub="dir_fill"]',
      },
      {
        id: "dir_save",
        title: "Сохраните",
        hint: "Нажмите «Сохранить» — направление появится в списке.",
        target: '[data-act-sub="dir_save"]',
      },
    ],
  },
  {
    id: "venue",
    index: 2,
    title: "Зал",
    short: "Добавьте место занятий",
    navItem: "venues",
    route: "/manage/app/venues",
    hint: "Добавьте зал. Без зала группу не создать.",
    success: "Зал есть.",
    predicate: "hasVenue",
    substeps: [
      {
        id: "venue_open",
        title: "Новый зал",
        hint: "Нажмите «+» или «Добавить зал».",
        target: '[data-act-sub="venue_open"]',
      },
      {
        id: "venue_fill",
        title: "Название зала",
        hint: "Укажите название — «Основной зал», «Зал №1»…",
        target: '[data-act-sub="venue_fill"]',
      },
      {
        id: "venue_save",
        title: "Сохраните",
        hint: "Нажмите «Сохранить».",
        target: '[data-act-sub="venue_save"]',
      },
    ],
  },
  {
    id: "group",
    index: 3,
    title: "Группа",
    short: "Создайте группу",
    navItem: "groups",
    route: "/manage/app/groups",
    hint: "Создайте первую группу — вид спорта и зал уже готовы.",
    success: "Группа создана.",
    predicate: "hasGroup",
    substeps: [
      {
        id: "group_new",
        title: "Новая группа",
        hint: "Откройте форму — направление и зал уже будут подставлены.",
        target: '[data-act-sub="group_new"]',
      },
      {
        id: "group_fill",
        title: "Название группы",
        hint: "Например «Дети 7–9» или «Взрослые». Направление и зал можно сменить.",
        target: '[data-act-sub="group_fill"]',
      },
      {
        id: "group_save",
        title: "Создайте группу",
        hint: "Нажмите «Создать» — группа появится в списке.",
        target: '[data-act-sub="group_save"]',
      },
    ],
  },
  {
    id: "schedule",
    index: 4,
    title: "Расписание",
    short: "Дни и время занятий",
    navItem: "groups",
    route: "/manage/app/groups",
    hint: "Задайте дни и время занятий для группы.",
    success: "Расписание сохранено.",
    predicate: "hasSchedule",
    substeps: [
      {
        id: "sched_pick_group",
        title: "Откройте группу",
        hint: "Нажмите на группу, чтобы задать расписание.",
        target: '[data-act-sub="sched_pick_group"]',
      },
      {
        id: "sched_fill",
        title: "Дни и время",
        hint: "Выберите дни (например Пн Ср Пт), время и нажмите «+ Добавить в расписание».",
        target: '[data-act-sub="sched_fill"]',
      },
      {
        id: "sched_save",
        title: "Сохраните расписание",
        hint: "Нажмите «Сохранить расписание» — занятия появятся в календаре.",
        target: '[data-act-sub="sched_save"]',
      },
    ],
  },
  {
    id: "plan",
    index: 5,
    title: "Тариф",
    short: "Создайте тариф",
    navItem: "memberships",
    route: "/manage/app/memberships",
    hint: "Тариф — шаблон абонемента (например 8 занятий).",
    success: "Тариф создан.",
    predicate: "hasPlan",
    substeps: [
      {
        id: "plan_open",
        title: "Создать тариф",
        hint: "Нажмите «Создать тариф».",
        target: '[data-act-sub="plan_open"]',
      },
      {
        id: "plan_fill",
        title: "Параметры тарифа",
        hint: "Название, число занятий или срок и цена — затем «Далее».",
        target: '[data-act-sub="plan_fill"]',
      },
      {
        id: "plan_save",
        title: "Сохраните тариф",
        hint: "Нажмите «Создать тариф».",
        target: '[data-act-sub="plan_save"]',
      },
    ],
  },
  {
    id: "client",
    index: 6,
    title: "Ученик",
    short: "Добавьте клиента",
    navItem: "clients",
    route: "/manage/app/clients",
    hint: "Добавьте первого ученика в группу.",
    success: "Ученик добавлен.",
    predicate: "hasClient",
    substeps: [
      {
        id: "client_open",
        title: "Добавить ученика",
        hint: "Нажмите «Добавить».",
        target: '[data-act-sub="client_open"]',
      },
      {
        id: "client_fill",
        title: "Имя и группа",
        hint: "Укажите имя и группу — без группы журнал не откроется.",
        target: '[data-act-sub="client_fill"]',
      },
      {
        id: "client_save",
        title: "Сохраните",
        hint: "Нажмите «Сохранить».",
        target: '[data-act-sub="client_save"]',
      },
    ],
  },
  {
    id: "issue",
    index: 7,
    title: "Абонемент",
    short: "Выдайте абонемент",
    navItem: "memberships",
    route: "/manage/app/memberships",
    hint: "Привяжите тариф к ученику.",
    success: "Абонемент выдан.",
    predicate: "hasIssuedMembership",
    substeps: [
      {
        id: "issue_open",
        title: "Выдать абонемент",
        hint: "Нажмите «Выдать абонемент».",
        target: '[data-act-sub="issue_open"]',
      },
      {
        id: "issue_fill",
        title: "Ученик и тариф",
        hint: "Выберите ученика и тариф — затем «Далее».",
        target: '[data-act-sub="issue_fill"]',
      },
      {
        id: "issue_save",
        title: "Выдайте",
        hint: "Нажмите «Выдать абонемент».",
        target: '[data-act-sub="issue_save"]',
      },
    ],
  },
  {
    id: "attendance",
    index: 8,
    title: "Журнал",
    short: "Отметьте в журнале",
    navItem: "journal",
    route: "/manage/app/journal",
    hint: "Первая отметка — и клуб «живой».",
    success: "Посещение отмечено.",
    predicate: "hasAttendance",
    substeps: [
      {
        id: "att_open",
        title: "Откройте занятие",
        hint: "Выберите занятие в журнале.",
        target: '[data-act-sub="att_open"]',
      },
      {
        id: "att_mark",
        title: "Отметьте явку",
        hint: "Нажмите галочку «Был» у ученика — это финиш быстрого старта.",
        target: '[data-act-sub="att_mark"]',
      },
    ],
  },
  {
    id: "parents",
    index: 9,
    title: "Родители",
    short: "Ссылка регистрации",
    navItem: "clients",
    route: "/manage/app/clients/registration",
    hint: "Скопируйте ссылку регистрации — родитель сам заведёт ребёнка в приложении.",
    success: "Ссылка для родителей готова.",
    predicate: "hasParentInvite",
    optional: true,
    substeps: [
      {
        id: "parents_link",
        title: "Скопируйте ссылку",
        hint: "Откройте или скопируйте ссылку регистрации.",
        target: '[data-act-sub="parents_link"]',
      },
    ],
  },
  {
    id: "overview",
    index: 10,
    title: "Сводка",
    short: "Главный экран клуба",
    navItem: "overview",
    route: "/manage/app",
    hint: "Главный экран клуба: занятия сегодня, клиенты, выручка и долги.",
    success: "Понятно, где смотреть сводку.",
    predicate: "hasVisitedOverview",
    trainingOnly: true,
    substeps: [
      {
        id: "overview_home",
        title: "Сводка дня",
        hint: "KPI и ближайшие занятия — начните день отсюда.",
        target: '[data-act-sub="overview_home"]',
      },
    ],
  },
  {
    id: "chat",
    index: 11,
    title: "Чат",
    short: "Сообщения клиентам",
    navItem: "chat",
    route: "/manage/app/chat",
    hint: "Переписка с родителями и учениками — все диалоги в одном месте.",
    success: "Чат найден.",
    predicate: "hasVisitedChat",
    trainingOnly: true,
    substeps: [
      {
        id: "chat_inbox",
        title: "Входящие",
        hint: "Список диалогов — ответы родителям и ученикам.",
        target: '[data-act-sub="chat_inbox"]',
      },
    ],
  },
  {
    id: "team",
    index: 12,
    title: "Команда",
    short: "Тренеры и админы",
    navItem: "team",
    route: "/manage/app/team",
    hint: "Тренеры и админы клуба — роли, доступы, приглашения по ссылке.",
    success: "Раздел команды понятен.",
    predicate: "hasVisitedTeam",
    trainingOnly: true,
    substeps: [
      {
        id: "team_list",
        title: "Состав команды",
        hint: "Кто имеет доступ к клубу и с какой ролью.",
        target: '[data-act-sub="team_list"]',
      },
    ],
  },
  {
    id: "finance",
    index: 13,
    title: "Финансы",
    short: "Платежи и должники",
    navItem: "finance",
    route: "/manage/app/finance?tab=debtors",
    hint: "Контроль оплат: должники, выручка, расходы и статистика.",
    success: "Финансы на месте.",
    predicate: "hasVisitedFinance",
    trainingOnly: true,
    substeps: [
      {
        id: "finance_debtors",
        title: "Должники",
        hint: "Список неоплаченных абонементов. Оплату принимают кнопкой «Записать оплату».",
        target: '[data-act-sub="finance_debtors"]',
      },
    ],
  },
  {
    id: "settings",
    index: 14,
    title: "Настройки",
    short: "Параметры клуба",
    navItem: "settings",
    route: "/manage/app/settings",
    hint: "Параметры клуба: название, регистрация, уведомления, брендинг.",
    success: "Настройки найдены.",
    predicate: "hasVisitedSettings",
    trainingOnly: true,
    substeps: [
      {
        id: "settings_club",
        title: "Клуб",
        hint: "Основные данные секции и режим регистрации.",
        target: '[data-act-sub="settings_club"]',
      },
    ],
  },
];

export function getActivationStep(id) {
  return ACTIVATION_STEPS.find((s) => s.id === id) || null;
}

export const TRAINING_QUICK_LESSON_IDS = [
  "group",
  "schedule",
  "plan",
  "client",
  "issue",
  "attendance",
];

export const TRAINING_FULL_LESSON_IDS = [
  ...TRAINING_QUICK_LESSON_IDS,
  "parents",
  "overview",
  "chat",
  "team",
  "finance",
  "settings",
];

/** Уроки полного обучения после быстрого старта */
export const TRAINING_EXTENDED_LESSON_IDS = TRAINING_FULL_LESSON_IDS.filter(
  (id) => !TRAINING_QUICK_LESSON_IDS.includes(id),
);

export function isQuickSetupComplete(setup = {}, skippedIds = []) {
  const flags = setupToActivationFlags(setup || {});
  const skipped = new Set(skippedIds || []);
  return TRAINING_QUICK_LESSON_IDS.every((id) => {
    const step = getActivationStep(id);
    if (!step) return true;
    return flags[step.predicate] || skipped.has(id);
  });
}

export function shouldShowFullTrainingBanner(tenantId, setup = {}, meta = {}) {
  // Пока скрыт: полное обучение / база знаний после быстрого старта.
  return false;
}

export function setupToActivationFlags(setup = {}) {
  const s = setup || {};
  return {
    hasDirection: Number(s.directions_count || 0) > 0,
    hasVenue: Number(s.branches_count || 0) > 0,
    hasGroup: Number(s.groups_count || s.specializations_count || 0) > 0,
    hasSchedule:
      Number(s.schedule_slots_count || 0) > 0 ||
      Number(s.training_sessions_count || 0) > 0,
    hasPlan: Number(s.membership_plans_count || 0) > 0,
    hasClient: Number(s.clients_count || 0) > 0,
    hasIssuedMembership:
      Boolean(s.has_issued_membership) ||
      Number(s.issued_memberships_count || 0) > 0,
    hasAttendance:
      Boolean(s.has_attendance) ||
      Number(s.attendance_present_count || 0) > 0,
    hasParentInvite: Boolean(s.parent_invite_seen),
  };
}

export function evaluateActivationSteps(setup = {}, skippedIds = []) {
  const activationSteps = ACTIVATION_STEPS.filter((s) => !s.trainingOnly);
  const flags = setupToActivationFlags(setup);
  const skipped = new Set(skippedIds || []);
  const completed = [];
  for (const step of activationSteps) {
    if (flags[step.predicate]) completed.push(step.id);
  }
  const doneSet = new Set([...completed, ...skipped]);
  const current = activationSteps.find((s) => !doneSet.has(s.id)) || null;
  const allDone = activationSteps.every(
    (s) => doneSet.has(s.id) || (s.optional && skipped.has(s.id)),
  );
  return {
    flags,
    completed,
    skipped: [...skipped],
    current,
    allDone,
    progressCount: completed.length,
    totalSteps: activationSteps.length,
  };
}

export const ACTIVATION_STORAGE_KEY = "baysport_activation_path";
export const QUICK_START_WELCOME_EVENT = "baysport:quick-start-welcome";
export const QUICK_START_ACCEPTED_EVENT = "baysport:quick-start-accepted";

const DEFAULT_ACTIVATION_META = {
  dismissed: false,
  skipped: [],
  parentInviteSeen: false,
  collapsed: true,
  substepByStep: {},
  tourVisits: {},
  fullTrainingBannerDismissed: false,
  onboardingOpened: false,
  welcomeSeen: false,
  mobileTourFromStart: false,
  /** Повторный проход: все подсказки по очереди, даже если клуб уже настроен. */
  walkthroughReplay: false,
  mobileTourDoneIds: [],
  startAfterOnboarding: false,
  tourPaused: false,
  celebrationSeen: false,
};

export function loadActivationMeta(tenantId) {
  if (!tenantId) {
    return { ...DEFAULT_ACTIVATION_META };
  }
  try {
    const raw = localStorage.getItem(`${ACTIVATION_STORAGE_KEY}_${tenantId}`);
    return raw
      ? { ...DEFAULT_ACTIVATION_META, ...JSON.parse(raw) }
      : { ...DEFAULT_ACTIVATION_META };
  } catch {
    return { ...DEFAULT_ACTIVATION_META };
  }
}

export function saveActivationMeta(tenantId, patch) {
  if (!tenantId) return;
  const prev = loadActivationMeta(tenantId);
  localStorage.setItem(
    `${ACTIVATION_STORAGE_KEY}_${tenantId}`,
    JSON.stringify({ ...prev, ...patch }),
  );
}

function navMenuLabel(navId) {
  const item = [...ADMIN_BOTTOM_TABS, ...MORE_ADMIN_NAV].find(
    (entry) => entry.id === navId,
  );
  return item?.label || navId;
}

export function navIdFromTourTarget(target) {
  const match = String(target || "").match(/data-act-nav="([^"]+)"/);
  return match?.[1] || null;
}

export function isMoreMenuNavSubstep(subId) {
  return Boolean(subId?.endsWith("_nav") && !subId?.endsWith("_nav_hub"));
}

export function getStepSubsteps(step, { isMobile = true } = {}) {
  const content = Array.isArray(step?.substeps) ? step.substeps : [];
  if (!step?.navItem) return content;

  const navSubsteps = [];

  if (!isCoreBottomTab(step.navItem) && isMobile) {
    navSubsteps.push({
      id: `${step.id}_nav_hub`,
      kind: "nav",
      title: "Ещё",
      hint: "Нажмите «Ещё» в меню внизу.",
      target: '[data-act-nav="more"]',
    });
  }

  navSubsteps.push({
    id: `${step.id}_nav`,
    kind: "nav",
    title: step.title,
    hint:
      isMobile && !isCoreBottomTab(step.navItem)
        ? `Нажмите «${navMenuLabel(step.navItem)}» в списке.`
        : NAV_BLURBS[step.id] || step.short || step.hint,
    target: `[data-act-nav="${step.navItem}"]`,
  });

  return [...navSubsteps, ...content];
}

export function normalizeTourSubstepId(stepId, subId) {
  if (!subId) return subId;
  if (
    stepId === "group" &&
    (subId === "group_name" ||
      subId === "group_direction" ||
      subId === "group_branch")
  ) {
    return "group_fill";
  }
  if (
    stepId === "plan" &&
    [
      "plan_type",
      "plan_name",
      "plan_duration",
      "plan_groups",
      "plan_price",
      "plan_freeze",
    ].includes(subId)
  ) {
    return "plan_fill";
  }
  if (
    stepId === "client" &&
    (subId === "client_name" || subId === "client_phone")
  ) {
    return "client_fill";
  }
  if (
    stepId === "issue" &&
    ["issue_mode", "issue_client", "issue_plan", "issue_date"].includes(subId)
  ) {
    return "issue_fill";
  }
  return subId;
}

/** Substeps for coach/tour — optionally skip nav hints (mobile / full training). */
export function getCoachSubsteps(step, { isMobile = true, skipNav = false } = {}) {
  const all = getStepSubsteps(step, { isMobile });
  if (!skipNav) return all;
  const content = all.filter((s) => s.kind !== "nav");
  return content.length ? content : all;
}

/** Click targets that auto-advance the tour (open / pick / mark). */
export const AUTO_ADVANCE_ON_CLICK_SUBSTEPS = new Set([
  "dir_open",
  "venue_open",
  "group_new",
  "sched_pick_group",
  "plan_open",
  "client_open",
  "issue_open",
  // att_open: только selectSession → att_mark (без авто-advance, иначе двойной шаг сразу завершает тур)
  "parents_link",
]);

export function isAutoAdvanceOnClickSubstep(subId) {
  return AUTO_ADVANCE_ON_CLICK_SUBSTEPS.has(subId);
}

/** Поля ввода/выбора — следующий шаг только по «Далее» или реальному действию */
export const MANUAL_TOUR_SUBSTEPS = new Set([
  "dir_fill",
  "venue_fill",
  "group_fill",
  // sched_fill: только через «+ Добавить в расписание», без «Далее»
  "plan_fill",
  "client_fill",
  "issue_fill",
]);

export function isManualTourSubstep(subId) {
  return MANUAL_TOUR_SUBSTEPS.has(subId);
}

/** Ознакомительные уроки полного обучения — подтверждение кнопкой «Понятно». */
export const TOUR_ACK_SUBSTEPS = new Set([
  "overview_home",
  "chat_inbox",
  "team_list",
  "finance_debtors",
  "settings_club",
]);

export function isTourAckSubstep(subId) {
  return TOUR_ACK_SUBSTEPS.has(subId);
}

export function getTourActionHint(subId) {
  if (subId?.endsWith("_nav_hub")) {
    return "";
  }
  if (isMoreMenuNavSubstep(subId)) {
    return "";
  }
  if (subId?.endsWith("_nav")) {
    return "";
  }
  if (isTourAckSubstep(subId)) {
    return "Посмотрите раздел — затем подтвердите ниже.";
  }
  if (subId === "sched_fill") {
    return "Выберите дни и нажмите «+ Добавить в расписание».";
  }
  if (isManualTourSubstep(subId)) {
    return "Заполните поля и нажмите «Далее».";
  }
  if (subId?.endsWith("_save") || subId === "att_mark") {
    return "";
  }
  return "Нажмите на подсвеченный элемент.";
}
